import { Link, useNavigate } from 'react-router-dom';
import { useBranding } from '../lib/branding';
import { useOutletAuth } from '../lib/auth/OutletAuthProvider';
import { useState } from 'react';

export default function TerminalSelector() {
  const { branding } = useBranding();
  const { outlet, authState } = useOutletAuth();
  const navigate = useNavigate();
  const [tapCount, setTapCount] = useState(0);
  const [tapTimeout, setTapTimeout] = useState<NodeJS.Timeout | null>(null);

  // Check if user has customer role
  const isCustomerRole = authState?.role === 'customer';

  // Hidden gesture: tap top-right corner 5 times within 3 seconds
  const handleCornerTap = () => {
    const newCount = tapCount + 1;
    setTapCount(newCount);

    // Clear existing timeout
    if (tapTimeout) {
      clearTimeout(tapTimeout);
    }

    // Reset counter after 3 seconds
    const timeout = setTimeout(() => {
      setTapCount(0);
    }, 3000);
    setTapTimeout(timeout);

    // Navigate to settings after 5 taps
    if (newCount >= 5) {
      setTapCount(0);
      if (tapTimeout) {
        clearTimeout(tapTimeout);
      }
      navigate('/settings');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50"
      style={{
        background: branding.colors.background,
      }}
    >
      {/* Hidden settings access point - top right corner */}
      <button
        onClick={handleCornerTap}
        className="absolute top-0 right-0 w-20 h-20 opacity-0 hover:opacity-10 transition-opacity"
        style={{ background: branding.colors.primary }}
        aria-label="Settings access"
      />
      
      <div className="max-w-4xl w-full">
        {/* Outlet Logo */}
        {branding.logos.primary && (
          <div className="flex justify-center mb-6">
            <img 
              src={branding.logos.primary} 
              alt={branding.businessName}
              className="h-20 object-contain"
            />
          </div>
        )}
        
        <h1 
          className="text-5xl font-bold text-center mb-4"
          style={{
            color: branding.colors.primary,
            fontFamily: branding.fonts.heading,
          }}
        >
          {branding.businessName}
        </h1>
        
        {outlet && (
          <p 
            className="text-center mb-2 text-lg font-medium"
            style={{ color: branding.colors.text }}
          >
            {outlet.name} - {outlet.location}
          </p>
        )}
        
        <p 
          className="text-center mb-12 text-lg"
          style={{ color: branding.colors.textMuted }}
        >
          {branding.tagline || 'Choose what you want to do'}
        </p>
        
        <div className={`grid grid-cols-1 ${isCustomerRole ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
          {/* Order */}
          <Link
            to="/order"
            className="group relative overflow-hidden rounded-3xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            style={{
              background: branding.colors.primary,
            }}
          >
            <div className="relative z-10">
              <div className="text-6xl mb-4">🛒</div>
              <h2 className="text-2xl font-bold mb-2">Order</h2>
              <p className="opacity-90">
                Place new orders, register customers, and handle payments
              </p>
            </div>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
          </Link>
          
          {/* Capture - Hidden for customer role */}
          {!isCustomerRole && (
            <Link
              to="/capture"
              className="group relative overflow-hidden rounded-3xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              style={{
                background: branding.colors.secondary,
              }}
            >
              <div className="relative z-10">
              <div className="text-6xl mb-4">📸</div>
              <h2 className="text-2xl font-bold mb-2">Capture</h2>
                <p className="opacity-90">
                  Scan QR codes and take photos with the camera
                </p>
              </div>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
            </Link>
          )}
          
          {/* Edit */}
          <Link
            to="/edit"
            className="group relative overflow-hidden rounded-3xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            style={{
              background: branding.colors.accent,
            }}
          >
            <div className="relative z-10">
              <div className="text-6xl mb-4">✨</div>
              <h2 className="text-2xl font-bold mb-2">Edit</h2>
              <p className="opacity-90">
                Edit photos, choose frames, and print
              </p>
            </div>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
          </Link>
        </div>
        
        <div 
          className="mt-12 text-center text-sm"
          style={{ color: branding.colors.textMuted }}
        >
          <p>Version 0.1.0</p>
        </div>
      </div>
    </div>
  );
}
